import { api } from "@/api/api";
import { AssetType, type EpisodeRejectionData } from "@/api/statistics-types";
import { RoleName } from "@/api/users-types";
import { NoPermissions } from "@/components/ui/NoPermissions";
import { useHasRole } from "@/hooks/use-has-role";
import { convertToCSV, downloadCSV } from "@/utils/csv-utils";
import { Download } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography
} from "@mui/material";
import { useState } from "react";

type CSVRow = {
  episodeId: string;
  state: string;
  page: string;
  imageId: string;
  maskId: string;
  storyTitle: string;
  category: string;
  subcategory: string;
  gender: string;
  assetType: string;
  rejectionReason: string;
  notes: string;
  url: string;
};

const CSV_HEADERS = [
  "Episode ID",
  "State",
  "Page",
  "Image ID",
  "Mask ID",
  "Story Title",
  "Category",
  "Subcategory",
  "Gender",
  "Asset Type",
  "Rejection Reason",
  "Notes",
  "URL",
];

function processRejectionData(episodes: EpisodeRejectionData[]): CSVRow[] {
  const rows: CSVRow[] = [];
  const baseUrl = window.location.origin;

  for (const episode of episodes) {
    // Add episode-level row
    rows.push({
      episodeId: episode.id,
      state: episode.state,
      page: "",
      imageId: "",
      maskId: "",
      storyTitle: episode.story,
      category: episode.category,
      subcategory: episode.subcategory,
      gender: episode.gender,
      assetType: AssetType.Episode,
      rejectionReason: "",
      notes: episode.rejectionNotes || "",
      url: `${baseUrl}/episodes/${episode.id}`,
    });

    // Process all images
    for (const image of episode.images || []) {
      rows.push({
        episodeId: episode.id,
        state: episode.state,
        page: image.page.toString(),
        imageId: image.id,
        maskId: "",
        storyTitle: episode.story,
        category: episode.category,
        subcategory: episode.subcategory,
        gender: episode.gender,
        assetType: AssetType.Image,
        rejectionReason:
          image.rejectionReasons.length > 0
            ? image.rejectionReasons[0].reason
            : "",
        notes: image.rejectionNotes || "",
        url: `${baseUrl}/images/${image.id}`,
      });
      // Process image rejections
      if (image.rejectionReasons.length > 1) {
        for (const reason of image.rejectionReasons.slice(1)) {
          rows.push({
            episodeId: episode.id,
            state: episode.state,
            page: image.page.toString(),
            imageId: image.id,
            maskId: "",
            storyTitle: episode.story,
            category: episode.category,
            subcategory: episode.subcategory,
            gender: episode.gender,
            assetType: AssetType.Image,
            rejectionReason: reason.reason,
            notes: "",
            url: "",
          });
        }
      }

      // Process mask
      if (image.mask) {
        rows.push({
          episodeId: episode.id,
          state: episode.state,
          page: image.page.toString(),
          imageId: image.id,
          maskId: image.mask.id,
          storyTitle: episode.story,
          category: episode.category,
          subcategory: episode.subcategory,
          gender: episode.gender,
          assetType: AssetType.Mask,
          rejectionReason:
            image.mask.rejectionReasons.length > 0
              ? image.mask.rejectionReasons[0].reason
              : "",
          notes: image.mask.rejectionNotes || "",
          url: `${baseUrl}/masks/${image.mask.id}`,
        });
        if (image.mask.rejectionReasons.length > 1) {
          for (const reason of image.mask.rejectionReasons.slice(1)) {
            rows.push({
              episodeId: episode.id,
              state: episode.state,
              page: image.page.toString(),
              imageId: image.id,
              maskId: image.mask.id,
              storyTitle: episode.story,
              category: episode.category,
              subcategory: episode.subcategory,
              gender: episode.gender,
              assetType: AssetType.Mask,
              rejectionReason: reason.reason,
              notes: "",
              url: "",
            });
          }
        }
      }
    }
  }

  return rows;
}

async function fetchAllRejectionReasons(
  onProgress?: (fetched: number) => void,
): Promise<EpisodeRejectionData[]> {
  let allEpisodes: EpisodeRejectionData[] = [];
  let cursor: string | undefined = undefined;

  do {
    const response = await api.statistics.rejectionReasons.get(
      cursor ? { cursor } : undefined,
    );
    allEpisodes = [...allEpisodes, ...response.result];
    cursor = response.nextCursor;

    if (onProgress) {
      onProgress(allEpisodes.length);
    }
  } while (cursor);

  return allEpisodes;
}

export function ReportsPage() {
  const [isDownloading, setIsDownloading] = useState(false);
  const [fetchProgress, setFetchProgress] = useState<number>(0);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      setFetchProgress(0);

      // Fetch all data with pagination
      const allEpisodes = await fetchAllRejectionReasons((fetched) => {
        setFetchProgress(fetched);
      });

      // Process data
      const csvRows = processRejectionData(allEpisodes);

      // Convert to CSV
      const csv = convertToCSV(CSV_HEADERS, csvRows);

      // Create and download file
      downloadCSV(
        `rejection-reasons-${new Date().toISOString().split("T")[0]}`,
        csv,
      );
    } catch (error) {
      console.error("Error downloading report:", error);
      alert("Failed to download report. Please try again.");
    } finally {
      setIsDownloading(false);
      setFetchProgress(0);
    }
  };

  const canView = useHasRole(RoleName.analyticsViewer);

  if (!canView) {
    return <NoPermissions />;
  }

  return (
    <Paper
      sx={{ p: 4, borderRadius: (theme) => theme.shape.borderRadius }}
      variant="outlined"
    >
      <Typography variant="h4" component="h2" gutterBottom>
        Reports
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Download rejection reasons report including Episode ID, Final State,
        Page, Image ID, Mask ID, Story Title, Category, Subcategory, Gender,
        Asset Type, Rejection Reason, Notes, and URL.
      </Typography>

      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownload}
          disabled={isDownloading}
          startIcon={
            isDownloading ? <CircularProgress size={20} /> : <Download />
          }
        >
          {isDownloading
            ? `Fetching data... (${fetchProgress} episodes)`
            : "Download CSV Report"}
        </Button>
      </Box>
    </Paper>
  );
}
